'use client';

import {
  Breakpoint,
  ICheckboxGroup,
  ICourse,
  SearchHookProps,
  useSearch,
  getSearchQuery,
  SearchSort,
  useWindowResize,
  LoadingSpinner,
  useQueryPagination,
  useQuerySortOrder,
  useQueryKeyword,
  useQueryFacets,
  mapFacetsToFilters,
  ApplicationType,
} from '@into-discovery/browsewebshared';
import { deviceDetect } from '@into-discovery/browsewebshared/src/data/device-detect';
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useRouter } from '@/navigation';
import SearchTitle from '@/components/atoms/search/search-result-title/search-result-title';
import SearchResults from '@/components/organisms/search-results/search-results';
import SearchFiltersMobile from '@/components/organisms/search-filters-mobile/search-filters-mobile';
import SearchFiltersDesktop from '@/components/organisms/search-filters-desktop/search-filters-desktop';
import { hitsViewed } from '@/services/helpers/analytics.helper';
import { useSearchParams } from 'next/navigation';
import { ISearchProps } from '@/models/search/ISearchProps';
import { Container, Row } from 'react-bootstrap';
import { AxiosResponse } from 'axios';

type Props = {
  pageProps: ISearchProps;
};

const searchApiSettings = {
  baseUrl: `${process.env.NEXT_PUBLIC_SEARCH_SERVICE_URL}/v2`,
  searchTimeOut: parseInt(process.env.NEXT_PUBLIC_SEARCH_TIMEOUT, 10),
  b2cFlag: true,
  applicationType: ApplicationType.IntoStudy,
};

function SearchProvider({ pageProps }: Props) {
  const [isBreakpointLgUp, setIsBreakpointLgUp] = useState(!deviceDetect.isMobile);
  const [isBreakpointMediumUp, setIsBreakpointMediumUp] = useState(!deviceDetect.isMobile);

  useWindowResize((newWidth) => {
    setIsBreakpointMediumUp(newWidth >= Breakpoint.Medium);
    setIsBreakpointLgUp(newWidth >= Breakpoint.Large);
  }, 100);

  const isMoreResultsLoading = useRef<boolean>(false);

  const router = useRouter();
  const searchParams = useSearchParams();

  const [isUniversitiesFacetsFullList, setIsUniversitiesFacetsFullList] = useState(false);

  const searchOptions = useMemo(() => {
    const querySortOrder = useQuerySortOrder(searchParams);
    const queryFacets = useQueryFacets(searchParams);
    const queryKeyword = useQueryKeyword(searchParams);
    const queryPagination = useQueryPagination(
      parseInt(process.env.NEXT_PUBLIC_SEARCH_DEFAULT_RESULTS_COUNT),
      parseInt(process.env.NEXT_PUBLIC_SEARCH_MAX_RESULTS_COUNT),
      searchParams,
    );

    const result: SearchHookProps & { queryPagination; searchQuery?: string } = {
      keyword: queryKeyword.getKeyword(),
      facets: queryFacets.getMappedFacets(),
      sortOrder: querySortOrder.getSortOrder(),
      page: queryPagination.getCurrentPage(),
      pageSize: pageProps?.defaultPageSize,
      searchApiSettings,
      queryPagination,
    };

    result.searchQuery = getSearchQuery(result);

    return result;
  }, [pageProps?.defaultPageSize, searchParams]);

  const loadNextPageHandler = useCallback(() => {
    if (isMoreResultsLoading.current) {
      return;
    }

    isMoreResultsLoading.current = true;

    const nextPageNumber = searchOptions.queryPagination.getCurrentPage() + 1;
    searchOptions.queryPagination.navigateToNextPage(nextPageNumber);
  }, [searchOptions.queryPagination]);

  // This is needed to support "Load more"
  const allCourses = useRef<ICourse[]>([]);

  const searchDataProcessing = useCallback(
    async (data: AxiosResponse<any, any>) => {
      const results = data?.data || [];

      const searchKeyword = decodeURI(results?.searchOptions?.keywords ?? ``);

      const searchQueryId = results?.searchOptions?.searchServiceId;

      let courses: ICourse[];
      let filters: ICheckboxGroup[] = [];

      if (isMoreResultsLoading.current) {
        isMoreResultsLoading.current = false;
        courses = [...allCourses.current, ...results.results];
      } else {
        courses = results.results;
      }

      allCourses.current = courses;

      if (courses.length === pageProps?.maxPageSize) {
        searchOptions.queryPagination.setResultsCount(pageProps?.maxPageSize);
      }

      const allFacets = results?.searchOptions?.facets;
      if (allFacets) {
        const selectedFacets = searchOptions.facets;

        filters = mapFacetsToFilters(allFacets, selectedFacets);
      }

      const totalCourses = results?.searchOptions?.pagingInfo?.total ?? 0;

      const isNextPageAvailable = searchOptions.queryPagination.isNextPageAvailable(totalCourses);

      const processingResult = {
        searchKeyword,
        searchQueryId,
        totalCourses,
        courses,
        filters,
        isNextPageAvailable,
      };

      return processingResult;
    },
    [
      pageProps?.maxPageSize,
      searchOptions.facets,
      searchOptions.queryPagination,
    ],
  );

  const {
    data: renderData,
    isLoading: isResultLoading,
    error,
  } = useSearch(
    searchOptions.searchQuery,
    searchOptions.searchApiSettings,
    {
      searchKeyword: '',
      searchQueryId: '',
      totalCourses: 0,
      courses: [],
      filters: [],
      isNextPageAvailable: true,
    },
    searchDataProcessing,
    [searchOptions],
  );

  return (
    <Container>
      {!!error && <p>Error loading data</p>}

      <Row data-insights-index={`${process.env.NEXT_PUBLIC_APPLICATION_NAME}-Search`}>
        {isBreakpointLgUp && (
          <div className="col-lg-3 d-md-none d-lg-block pt-2">
            <div id="left-sidebar" className="position-relative">
              {!isResultLoading && (
                <SearchFiltersDesktop
                  filters={renderData.filters}
                  showAll={isUniversitiesFacetsFullList}
                  toggleShowAll={setIsUniversitiesFacetsFullList}
                />
              )}
            </div>
          </div>
        )}
        <div className="col-lg-9">
          {!isBreakpointLgUp && (
            <div className="col d-lg-none">
              {!isResultLoading && (
                <SearchFiltersMobile
                  filters={renderData.filters}
                  sortOptions={pageProps?.sortOptions}
                  isBreakpointLgUp={isBreakpointLgUp}
                />
              )}
            </div>
          )}
          {isBreakpointLgUp && (
            <div className="row py-2 bg-white">
              <div className="col d-flex justify-content-between align-items-center">
                <SearchTitle
                  searchTitle={renderData.searchKeyword}
                  countOfResult={renderData.totalCourses}
                />
                <SearchSort
                  sortOptions={pageProps?.sortOptions}
                  router={router}
                  searchParams={searchParams}
                  isBreakpointLgUp={isBreakpointLgUp}
                />
              </div>
            </div>
          )}
          {!isBreakpointLgUp && (
            <div className="mb-2">
              <SearchTitle
                searchTitle={renderData.searchKeyword}
                countOfResult={renderData.totalCourses}
              />
            </div>
          )}

          <div className={isResultLoading ? `d-block` : `d-none`}>
            <LoadingSpinner src="/next-assets/loaders/into.svg" />
          </div>

          <SearchResults
            courses={renderData.courses}
            totalCourses={renderData.totalCourses}
            defaultPageSize={pageProps?.defaultPageSize}
            loadNextPage={loadNextPageHandler}
            isDataLoading={isResultLoading && isMoreResultsLoading.current}
            isNextPageAvailable={renderData.isNextPageAvailable}
            searchQueryId={renderData.searchQueryId}
            isBreakpointLgUp={isBreakpointLgUp}
            isBreakpointMediumUp={isBreakpointMediumUp}
          />
          {!isResultLoading && hitsViewed()}
        </div>
      </Row>
    </Container>
  );
}

export default memo(SearchProvider);
